import { useState } from 'react'
import toast from 'react-hot-toast'
import {
  estimateMissionRhum,
  usePvpRaidEstimate,
  useLaunchPvpRaid,
  useMyFleet,
  type MapIslandDto,
} from '@/api/hooks'
import { CombatPrimer } from '@/components/CombatPrimer'
import { lossSeverity, LOSS_SEVERITY_CLASS, formatAvgLosses, formatLossRange } from '@/lib/raidLosses'
import { HintBubble } from '@/components/game/ui'
import { RumIcon, CombatIcon, ShipIcon, PennantIcon } from '@/components/game/GameIcons'
import { toggleShipSelection, selectManyShips } from '@/components/game/map/fleetSelection'
import {
  RationsLabel,
  ShipPickerList,
  ThreatReadout,
  IntelStalenessMarker,
} from '@/components/game/map/launcherBits'
import { combatMeta } from '@/components/game/map/shipPicker/ShipPickerRow'
import { missionFleetCap } from '@/lib/fleetGroups'
import { tripDurationSeconds, TripDuration, convoySpeedPerHour, ConvoyPace } from '@/components/game/map/tripDuration'

/**
 * Why a PLAYER island can't be raided right now — the shared `PvpRules` verdict
 * mapped to a player-facing reason. 'ok' is the only raidable verdict; the rest
 * gate the launch and surface here as the disabled button's label/tooltip.
 */
const PVP_RAID_BLOCK_REASON: Record<NonNullable<MapIslandDto['raidableReason']>, string | null> = {
  ok: null,
  self: 'Ta propre île',
  shielded: 'Sous bouclier (nouveau venu)',
  immune: "Vient d'être pillée",
  too_weak: 'Cible trop faible',
}

/**
 * Raid a foreign PLAYER island. Reuses the raid fleet-picker (ordered multi-ship
 * checkbox list) + the scouted threat readout, but wired to {@see useLaunchPvpRaid}
 * and gated by the shared `PvpRules` verdict: the launch is DISABLED whenever the
 * island isn't `raidable`, surfacing WHY ({@see PVP_RAID_BLOCK_REASON}). Shares the
 * picker/readout with the NPC {@see RaidLauncher}; the win%/loss estimate
 * ({@see usePvpRaidEstimate}) is scout-gated — shown only once the target has been
 * reconnoitred.
 */
export function PvpRaidLauncher({ island }: { island: MapIslandDto }) {
  const { data: fleet } = useMyFleet()
  const launch = useLaunchPvpRaid()
  const dockedShips = fleet?.ships.filter((s) => s.status === 'docked') ?? []
  const fleetCap = missionFleetCap(fleet?.shipyard?.level)

  // Ordered selection (first = lead), mirrors the NPC raid picker.
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const selected = selectedIds
    .map((id) => dockedShips.find((s) => s.id === id))
    .filter((s): s is NonNullable<typeof s> => Boolean(s))

  // Live win-rate estimate from a backend combat simulation of this exact fleet —
  // GATED on having scouted the target (the endpoint 422s otherwise), so the hook
  // stays disabled until recon lands.
  const estimate = usePvpRaidEstimate(selectedIds, island.id, island.scouted)

  const toggleShip = (id: number) => {
    setSelectedIds((prev) =>
      toggleShipSelection(prev, id, dockedShips, fleetCap, () => toast.error(`Flotte au max (${fleetCap} navires).`)),
    )
  }

  const selectMany = (ids: number[]) => {
    setSelectedIds((prev) =>
      selectManyShips(prev, ids, dockedShips, fleetCap, () => toast.error(`Flotte au max (${fleetCap} navires).`)),
    )
  }

  // Rhum upkeep estimate — slowest ship drives the duration, then Σ crew × hours
  // (matches the NPC raid; the server is authoritative at launch).
  const origin = fleet?.island
  const oneWayDistance = origin ? Math.sqrt((island.x - origin.x) ** 2 + (island.y - origin.y) ** 2) : 0
  const slowestSpeed = convoySpeedPerHour(selected, fleet?.travelSpeedForgeMult ?? 1)
  const roundTripHours = slowestSpeed > 0 ? (oneWayDistance * 2) / slowestSpeed : 0
  const tripSeconds = origin ? tripDurationSeconds(slowestSpeed, origin, island) : 0
  const rhumEstimate = estimateMissionRhum(
    selected,
    Math.max(60, roundTripHours * 3600),
  )
  const rhumAvailable = fleet?.resources?.rum ?? 0
  const notEnoughRhum = selected.length > 0 && rhumEstimate > rhumAvailable

  // The shared PvpRules verdict: 'ok' is raidable, everything else gates the launch
  // with a human reason shown on the disabled button + its tooltip.
  const blockReason = island.raidableReason !== null ? PVP_RAID_BLOCK_REASON[island.raidableReason] : null
  const blocked = !island.raidable
  const canLaunch = !blocked && !launch.isPending && selected.length > 0 && !notEnoughRhum

  if (dockedShips.length === 0) {
    return (
      <div className="bg-rum-700/30 rounded-lg p-3 space-y-2 border border-rum-500/30">
        <p className="text-xs text-rum-500 italic">
          Aucun navire à quai pour ce raid.
        </p>
      </div>
    )
  }

  const buttonLabel = launch.isPending
    ? 'Lancement…'
    : blocked
      ? (blockReason ?? 'Raid impossible')
      : notEnoughRhum
        ? 'Rhum insuffisant'
        : null

  return (
    <div className="bg-rum-700/25 rounded-lg p-3 space-y-3 border border-rum-500/30">
      <p className="text-xs uppercase tracking-wider flex items-center gap-1.5">
        <CombatIcon className="w-4 h-4 text-rum-500" /><span className="text-rum-500">Raid</span>
      </p>

      {island.threat && (
        <div className="space-y-1">
          <ThreatReadout threat={island.threat} />
          {/* Ce que l'éclaireur a vu du PORT (fiche #27) : les coques au mouillage,
              et si leurs équipages étaient à bord. Une cible au branle-bas rend les
              coups, donc l'ignorer ferait sur-promettre l'estimation.
              Rendu SEULEMENT si le snapshot existe : une intel antérieure à la
              feature vaut « on ne sait pas », jamais « aucun navire ». */}
          <ScoutedFleetAtQuay fleet={island.fleetAtQuay} />
          {/* Intel is frozen at scout-time (durable opacity) — surface WHEN it was
              taken so the attacker knows the readout may be stale. */}
          {island.scoutedAt && <IntelStalenessMarker scoutedAt={island.scoutedAt} />}
        </div>
      )}

      <ShipPickerList
        ships={dockedShips}
        selectedIds={selectedIds}
        onToggle={toggleShip}
        cap={fleetCap}
        onSelectMany={selectMany}
        onClearAll={() => setSelectedIds([])}
        meta={combatMeta}
      />

      {selected.length > 0 && (
        <div className="text-xs space-y-1 bg-ocean-900/50 rounded p-2 border border-sand-500/15">
          <div className="flex justify-between text-ocean-300">
            <RationsLabel />
            <span className={`tabular-nums inline-flex items-center gap-1 ${notEnoughRhum ? 'text-red-400 font-semibold' : 'text-sand-300'}`}>
              ≈ {rhumEstimate} <RumIcon className="w-3.5 h-3.5" />
              {notEnoughRhum && <span className="ml-1">(banque : {rhumAvailable})</span>}
            </span>
          </div>
          <div className="flex justify-between text-ocean-300">
            <span>Trajet</span>
            <TripDuration seconds={tripSeconds} />
          </div>
          <ConvoyPace ships={selected} tripSeconds={tripSeconds} />
          {/* Combat odds — only when scouted (the éclaireur's payoff). Without
              recon we show a muted nudge instead of numbers; the backend would
              422 the estimate anyway. NO loot line: the defender's stock stays
              hidden (anti loot-scouting). Mirrors the NPC RaidLauncher block. */}
          {!island.scouted ? (
            <div className="border-t border-sand-500/15 pt-1.5 mt-1.5">
              <p className="text-ocean-400 text-[11px] leading-snug italic">
                Envoie un éclaireur pour estimer tes chances de victoire.
              </p>
            </div>
          ) : (
            <div className="border-t border-sand-500/15 pt-1.5 mt-1.5 space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-ocean-200">Chance de victoire</span>
                {estimate.isFetching || estimate.data === undefined ? (
                  <span className="text-ocean-400 text-[11px]">simulation…</span>
                ) : (
                  <span
                    className={`font-display tabular-nums ${
                      estimate.data.winRate >= 0.6
                        ? 'text-green-300'
                        : estimate.data.winRate >= 0.35
                          ? 'text-sand-300'
                          : 'text-rum-500'
                    }`}
                  >
                    ≈ {Math.round(estimate.data.winRate * 100)}%
                  </span>
                )}
              </div>
              {estimate.data !== undefined && !estimate.isFetching && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="group/tip relative cursor-help text-ocean-200 underline decoration-dotted decoration-ocean-500/50 underline-offset-2">
                      Pertes estimées
                      <HintBubble
                        className="w-64"
                        label={
                          <>
                            Fourchette où tombent tes pertes ~8 fois sur 10 (en moyenne {formatAvgLosses(estimate.data.avgLosses)}). Une <span className="text-sand-200">victoire</span> veut dire que l'ennemi est anéanti, <span className="text-sand-200">pas</span> que tu rentres intact.
                          </>
                        }
                      />
                    </span>
                    <span
                      className={`font-display tabular-nums ${LOSS_SEVERITY_CLASS[lossSeverity(estimate.data.lossesHigh, estimate.data.fleetSize)]}`}
                    >
                      {formatLossRange(estimate.data.lossesLow, estimate.data.lossesHigh)}
                    </span>
                  </div>
                  {lossSeverity(estimate.data.lossesHigh, estimate.data.fleetSize) !== 'none' && (
                    <p className="text-ocean-400 text-[11px] leading-snug">
                      ⚓ Vaincre = couler l'ennemi, pas en sortir indemne. Tes coques les moins blindées encaissent en premier.
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-ocean-300 leading-snug">
        Le butin pillé dépend des entrepôts du joueur (or, bois ou rhum), plafonné par les règles anti-griefing.
        Tout navire coulé est perdu définitivement.
      </p>

      <CombatPrimer />

      <button
        onClick={() => {
          if (selected.length === 0 || blocked) return
          const shipIds = selected.map((s) => s.id)
          launch.mutate(
            { shipIds, islandId: island.id },
            {
              onSuccess: () =>
                toast.success(`${selected.length} navire(s) en route vers ${island.name}.`),
              onError: (err) =>
                toast.error(err.message || 'Raid impossible'),
            },
          )
          setSelectedIds([])
        }}
        disabled={!canLaunch}
        title={blocked ? (blockReason ?? undefined) : undefined}
        className="w-full text-sm px-3 py-1.5 rounded-md bg-rum-500 text-ocean-50 font-semibold hover:bg-rum-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
      >
        {buttonLabel ?? <><CombatIcon className="w-4 h-4" /> Lancer la flotte ({selected.length})</>}
      </button>
    </div>
  )
}

/**
 * La flotte à quai relevée au dernier scout. Figée avec le reste de l'intel, donc
 * à lire avec le marqueur de fraîcheur : la flotte a pu reprendre la mer depuis.
 *
 * Ne rend RIEN quand le snapshot est absent (intel antérieure à la feature) : un
 * « port vide » affiché par défaut serait un mensonge, et pousserait le joueur à
 * attaquer une île qu'il croit sans défense navale.
 */
function ScoutedFleetAtQuay({ fleet }: { fleet: MapIslandDto['fleetAtQuay'] }) {
  if (!fleet) return null

  const total = fleet.composition.reduce((n, e) => n + e.count, 0)

  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      <span className="text-ocean-300 uppercase tracking-wider">Au port</span>
      {total === 0 ? (
        <span className="text-ocean-300 italic">aucun navire</span>
      ) : (
        fleet.composition.map((entry) => (
          <span
            key={entry.shipType}
            className="inline-flex items-center gap-1 rounded bg-ocean-900/60 px-2 py-0.5 text-sand-100 tabular-nums"
          >
            {entry.count}
            <ShipIcon type={entry.shipType} className="w-3.5 h-3.5 text-sand-300" />
          </span>
        ))
      )}
      {fleet.mobilized && (
        <span className="inline-flex items-center gap-1 rounded border border-amber-400/45 bg-amber-400/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-300">
          <PennantIcon className="w-3 h-3" aria-hidden="true" />
          Branle-bas
        </span>
      )}
    </div>
  )
}
